// Gravação do "Corrigir dados" das telas de validação e de cálculo da fatura.
//
// O número da UC vai num UPDATE próprio e só quando mudou. Junto com os outros campos,
// uma recusa do banco por causa dele (duplicata do mês, fechamento de agrupamento)
// derrubava a gravação inteira — e a tela não olhava o erro: o analista via o número
// voltar ao antigo sem saber por quê. Foi o caso da fatura 15210 (OUT/2026): o número
// certo já tinha outra fatura ativa no mês, com boleto enviado, e o banco recusava.
//
// Também não vai a cada pausa da digitação: o salvamento automático gravava o número
// pela metade ("2.797", "2797"...), e cada gravação dispara as travas e a herança de
// empresa parceira com um número que não é de UC nenhuma. Ele só é gravado ao concluir
// a edição (ou ao fechar a tela no meio dela).
//
// A parte pura roda em `node --test`:
//   node --experimental-strip-types --test src/lib/__tests__/correcaoDadosFatura.test.ts

import type { SupabaseClient } from '@supabase/supabase-js';

export interface ResultadoCorrecao {
    /** Os outros campos não foram gravados. */
    erro: string | null;
    /** O número da UC não foi gravado (os outros campos podem ter sido). */
    erroUc: string | null;
}

/** O número digitado é outro, desconsiderando espaço nas pontas (o banco apara). */
export function ucFoiAlterada(digitada: unknown, atual: unknown): boolean {
    return String(digitada ?? '').trim() !== String(atual ?? '').trim();
}

/** Mensagem do banco com o que o analista deve fazer, quando dá para dizer. */
export function explicarErroDaUc(mensagem: string): string {
    if (/duplicata bloqueada/i.test(mensagem)) {
        return `${mensagem} Se for a mesma conta da Equatorial, esta fatura é repetida: exclua esta em vez de trocar o número.`;
    }
    return mensagem;
}

/**
 * Grava os campos corrigidos e, se `uc` vier e o número tiver mudado, o número da UC
 * num segundo UPDATE. Nunca lança: devolve o motivo de cada recusa.
 */
export async function gravarCorrecaoFatura(
    supabase: SupabaseClient,
    faturaId: string | number,
    campos: Record<string, unknown>,
    uc?: { digitada: unknown; atual: unknown },
): Promise<ResultadoCorrecao> {
    const resultado: ResultadoCorrecao = { erro: null, erroUc: null };

    const { error } = await supabase.from('faturas').update(campos).eq('id', faturaId);
    if (error) resultado.erro = error.message || 'O banco recusou a gravação.';

    if (uc && ucFoiAlterada(uc.digitada, uc.atual)) {
        const nova = String(uc.digitada ?? '').trim();
        if (!nova) {
            resultado.erroUc = 'O número da UC não pode ficar vazio.';
        } else {
            const { error: erroUc } = await supabase.from('faturas').update({ uc_codigo: nova }).eq('id', faturaId);
            if (erroUc) resultado.erroUc = explicarErroDaUc(erroUc.message || 'O banco recusou o número da UC.');
        }
    }

    return resultado;
}
